import _ from 'lodash'
import { NvApi } from '@nv/react-commons/src/Services'
import { NavigateFunction } from 'react-router'
import { dashApi } from 'services/api'
import { trackEvent } from 'utils/GoogleAnalytics/GAEventTracker'
import { INVALID_LOGIN, LOGIN } from 'utils/GoogleAnalytics/events'
import { mapShippers, mapSubShippers } from 'containers/LoginForm/saga'
import { shipperCreators, userCreators } from 'containers/Base/redux'
import { DEFAULT_LOCALE, ROUTES } from 'containers/Base/constants'
import { languageCreators } from 'containers/LanguageProvider/redux'
import { getLocale } from 'utils/locale'
import { mixpanelTrackLogIn } from 'components/Mixpanel/helpers'
import { mpTrackFirstLoginDate } from 'containers/FPLMixpanel/helpers'
import { SALES_CHANNEL } from 'enums/saleschannel'
import isEmpty from 'lodash/isEmpty'
import { COUNTRY_QUERY_PARAM, FROM_LOGIN_QUERY_PARAM } from '../SignUpWrapper/SignUpWrapper.constants'

type loginArgs = {
  navigate: NavigateFunction
  dispatch: (action: any) => void
  email?: {
    email: string
    password: string
  }
  phone?: {
    contact: string
    systemId: string
    otp: string
  }
}

export const login = async ({ dispatch, email, phone, navigate }: loginArgs) => {
  if ((email && phone) || (!email && !phone)) {
    throw new Error('Please provide either email or phone, but not both.')
  }

  const loginResponse = await dashApi.loginV2(email || phone)

  const user = loginResponse.data
  let shippers = mapShippers(user)
  let allowFulfillmentService = false

  if (!loginResponse.ok) {
    trackEvent(INVALID_LOGIN)
    return loginResponse
  }

  if (_.isEmpty(shippers)) {
    return { ok: false }
  }

  NvApi.setAuthToken(user.accessToken)
  const ocSettings = await dashApi.getSettings('order_create', user.defaultShipperId)
  const fulfillmentSettings = await dashApi.getSettings('cross_border', user.defaultShipperId)

  let getSubShippersResponse
  let isCorporateHQ
  if (ocSettings.ok) {
    isCorporateHQ = _.get(ocSettings, 'data.orderCreate.isCorporateHq')
    const isMarketplace = _.get(ocSettings, 'data.orderCreate.isMarketplace')
    if (isCorporateHQ || isMarketplace) {
      getSubShippersResponse = await dashApi.getSubShippers(user.defaultShipperId)
    }
  }

  if (user.masterShipper) {
    const corporateBranchResponse = await dashApi.getShipperBranch(user.masterShipper.id, user.defaultShipperId)
    if (corporateBranchResponse?.ok) {
      shippers[user.defaultShipperId].externalRef = corporateBranchResponse.data.externalRef
    }
    const countResp = await dashApi.getShippersCount(user.masterShipper.id)
    if (countResp?.ok) {
      const count = countResp?.data?.data ?? {}
      dispatch(shipperCreators.getSubShippersCount(count))
    }
  }
  if (getSubShippersResponse?.ok) {
    const subShippers = mapSubShippers(getSubShippersResponse, isCorporateHQ)
    shippers = _.merge(shippers, subShippers)
  }

  if (fulfillmentSettings?.ok) {
    allowFulfillmentService = fulfillmentSettings.data?.crossBorder?.allowFulfillmentService
  }

  dispatch(shipperCreators.getShipperSuccess(shippers))
  const locale = user?.locale || DEFAULT_LOCALE
  NvApi.setLanguage(locale)
  dispatch(languageCreators.changeLocale(getLocale(locale)))
  dispatch(shipperCreators.selectShipper(user.defaultShipperId))

  const { salesChannel, liaisonName, liaisonContact } = user.shippers[0]

  const isSS = salesChannel === SALES_CHANNEL.SelfServe
  const hasCompletedSetupStep = !isEmpty(liaisonName) && !isEmpty(liaisonContact) && liaisonName !== liaisonContact

  if (isSS && !hasCompletedSetupStep) {
    dispatch(userCreators.signUpSetupPending())
    navigate(
      `${ROUTES.SIGNUP_PROFILE}?${FROM_LOGIN_QUERY_PARAM}=true&${COUNTRY_QUERY_PARAM}=${user?.shippers?.[0]?.country}`
    )
  } else {
    sessionStorage.clear()
    dispatch(userCreators.clearSignUpSetupPending())
    mixpanelTrackLogIn(email ? 'Email' : 'Phone Number', user)
    mpTrackFirstLoginDate(allowFulfillmentService)
    trackEvent(LOGIN)
  }
  dispatch(userCreators.loginSuccess(_.omit(user, 'shippers')))
  return { ok: true }
}
